import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers } from '../services/userService';
import { importUsersFromCSV, exportUsersToCSV, downloadImportTemplate, updateUserRole, updateUserEmailMarketing } from '../services/adminService';
import { useAppContext } from '../context/AppContext';

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all'); // all, billable, non_billable
    const [showCSVImportModal, setShowCSVImportModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const { currentUser } = useAppContext();
    const navigate = useNavigate();


    useEffect(() => {
        if (currentUser && !['admin', 'admin'].includes(currentUser.role)) {
            navigate('/');
            return;
        }

        const fetchUsers = async () => {
            try {
                setLoading(true);
                const data = await getUsers();
                setUsers(data);
            } catch (error) {
                console.error('Error fetching users:', error);
            } finally {
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchUsers();
        }
    }, [currentUser, navigate]);

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.full_name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filter === 'all' ||
            (filter === 'billable' && user.billing_status !== 'non_billable') ||
            (filter === 'non_billable' && user.billing_status === 'non_billable');

        return matchesSearch && matchesFilter;
    });

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateUserRole(userId, newRole);
            setUsers(users.map(user =>
                user.id === userId ? { ...user, role: newRole } : user
            ));
        } catch (error) {
            console.error('Error updating user role:', error);
            alert(error.response?.data?.detail || 'Failed to update role');
        }
    };

    const handleEmailMarketingToggle = async (userId, currentValue) => {
        try {
            await updateUserEmailMarketing(userId, !currentValue);
            setUsers(users.map(user =>
                user.id === userId ? { ...user, email_marketing: !currentValue } : user
            ));
        } catch (error) {
            console.error('Error updating email marketing:', error);
            alert(error.response?.data?.detail || 'Failed to update email preference');
        }
    };

    const handleCSVImport = async () => {
        if (!selectedFile) return;

        setImporting(true);
        setImportResults(null);

        try {
            const results = await importUsersFromCSV(selectedFile);
            setImportResults(results);

            // Refresh user list
            const data = await getUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error importing users:', error);
            setImportResults({
                success: false,
                error: error.response?.data?.detail || 'Failed to import users'
            });
        } finally {
            setImporting(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setSelectedFile(file);
            setImportResults(null);
        } else {
            alert('Please select a valid CSV file');
        }
    };

    const handleExportCSV = async () => {
        try {
            await exportUsersToCSV();
        } catch (error) {
            console.error('Error exporting users:', error);
            alert('Failed to export users');
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadImportTemplate();
        } catch (error) {
            console.error('Error downloading template:', error);
            alert('Failed to download template');
        }
    };

    const getBillingStatusBadge = (status) => {
        const styles = {
            billable: 'bg-green-100 text-green-800',
            non_billable: 'bg-gray-100 text-gray-800',
            trial: 'bg-blue-100 text-blue-800',
            exempt: 'bg-purple-100 text-purple-800'
        };

        const labels = {
            billable: 'Billable',
            non_billable: 'Not Billable',
            trial: 'Trial',
            exempt: 'Exempt'
        };

        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.billable}`}>
                {labels[status] || 'Billable'}
            </span>
        );
    };

    if (loading) {
        return <div className="text-center py-10">Loading users...</div>;
    }

    return (
        <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                <p className="mt-2 text-sm text-gray-600">Manage users and billing status for your organization.</p>
            </div>

            {/* Controls */}
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                    <div className="w-full sm:w-80">
                        <input
                            type="text"
                            placeholder="Search users..."
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="block border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                        <option value="all">All Users</option>
                        <option value="billable">Billable Users</option>
                        <option value="non_billable">Non-Billable Users</option>
                    </select>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={handleExportCSV}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <i className="fas fa-download mr-2"></i>
                        Export CSV
                    </button>
                    <button
                        onClick={() => setShowCSVImportModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <i className="fas fa-file-csv mr-2"></i>
                        Import CSV
                    </button>
                    <button
                        onClick={() => navigate('/admin/invitations')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                        <i className="fas fa-envelope mr-2"></i>
                        Invite Users
                    </button>
                </div>
            </div>

            {/* User Table */}
            <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marketing Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10">
                                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {user.full_name.charAt(0)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                                            <div className="text-sm text-gray-500">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.source === 'slack' && <i className="fab fa-slack text-purple-600"></i>}
                                    {user.source === 'web' && <i className="fas fa-globe text-blue-600"></i>}
                                    {user.source === 'invitation' && <i className="fas fa-envelope text-green-600"></i>}
                                    <span className="ml-2 capitalize">{user.source || 'web'}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getBillingStatusBadge(user.billing_status || 'billable')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.last_activity_date ? new Date(user.last_activity_date).toLocaleDateString() : 'Never'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <button
                                        onClick={() => handleEmailMarketingToggle(user.id, user.email_marketing !== false)}
                                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            user.email_marketing !== false
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}
                                    >
                                        {user.email_marketing !== false ? 'Subscribed' : 'Unsubscribed'}
                                    </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex space-x-2">
                                        {user.id !== currentUser?.id && (
                                            <button
                                                onClick={() => handleRoleChange(user.id, user.role === 'admin' ? 'user' : 'admin')}
                                                className="text-indigo-600 hover:text-indigo-900"
                                                title={user.role === 'admin' ? 'Remove admin' : 'Make admin'}
                                            >
                                                {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* CSV Import Modal */}
            {showCSVImportModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Import Users from CSV</h3>
                        </div>
                        <div className="px-6 py-4">
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 mb-4">
                                    Upload a CSV file to bulk import users. The CSV must include <strong>email</strong> and <strong>full_name</strong> columns.
                                    Optional columns: <strong>department</strong>, <strong>job_title</strong>.
                                </p>
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="mb-4 text-sm text-blue-600 hover:text-blue-800 underline"
                                >
                                    📄 Download CSV Template
                                </button>

                                <div className="mt-4">
                                    <label className="block">
                                        <span className="sr-only">Choose CSV file</span>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleFileSelect}
                                            className="block w-full text-sm text-gray-500
                                                file:mr-4 file:py-2 file:px-4
                                                file:rounded-md file:border-0
                                                file:text-sm file:font-semibold
                                                file:bg-blue-50 file:text-blue-700
                                                hover:file:bg-blue-100"
                                        />
                                    </label>
                                    {selectedFile && (
                                        <p className="mt-2 text-sm text-gray-600">
                                            Selected: <strong>{selectedFile.name}</strong>
                                        </p>
                                    )}
                                </div>
                            </div>

                            {importResults && (
                                <div className={`mt-4 p-4 rounded-md ${importResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50'}`}>
                                    {importResults.success ? (
                                        <>
                                            <h4 className="text-sm font-medium text-green-800 mb-2">Import Results</h4>
                                            <div className="text-sm text-green-700">
                                                <p>✅ Total rows: {importResults.summary.total_rows}</p>
                                                <p>✅ Imported: {importResults.summary.imported}</p>
                                                {importResults.summary.skipped > 0 && (
                                                    <p>⚠️ Skipped: {importResults.summary.skipped}</p>
                                                )}
                                                {importResults.summary.failed > 0 && (
                                                    <p>❌ Failed: {importResults.summary.failed}</p>
                                                )}
                                            </div>
                                            {importResults.summary.imported > 0 && (
                                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                                    <p className="text-sm text-blue-800">
                                                        📧 Invitation emails have been sent to all imported users.
                                                        <button
                                                            onClick={() => navigate('/admin/invitations')}
                                                            className="ml-2 font-semibold underline hover:text-blue-900"
                                                        >
                                                            View pending invitations →
                                                        </button>
                                                    </p>
                                                </div>
                                            )}
                                            {importResults.details.skipped.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-sm font-medium text-gray-700">Skipped users:</p>
                                                    <ul className="text-xs text-gray-600 list-disc list-inside">
                                                        {importResults.details.skipped.map((skip, idx) => (
                                                            <li key={idx}>Row {skip.row}: {skip.email} - {skip.reason}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {importResults.details.errors.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-sm font-medium text-gray-700">Errors:</p>
                                                    <ul className="text-xs text-gray-600 list-disc list-inside">
                                                        {importResults.details.errors.map((error, idx) => (
                                                            <li key={idx}>Row {error.row}: {error.email} - {error.reason}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <h4 className="text-sm font-medium text-red-800 mb-2">Import Failed</h4>
                                            <p className="text-sm text-red-700">{importResults.error}</p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-4 rounded-b-lg">
                            <button
                                onClick={() => {
                                    setShowCSVImportModal(false);
                                    setSelectedFile(null);
                                    setImportResults(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleCSVImport}
                                disabled={!selectedFile || importing}
                                className="px-4 py-2 border border-transparent rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
                            >
                                {importing ? 'Importing...' : 'Import Users'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUserManagement;